/* Shared metadata for the Hire Professionals module.
 * Keeps category labels, taglines and theme accents in one place so they
 * stay consistent across listing, profile, inquiry, and dashboard views.
 */
import {
  Compass,
  ShieldCheck,
  Zap,
  Armchair,
  HardHat,
  Sparkles,
} from "lucide-react";

export const PRO_CATEGORIES = [
  {
    key: "architect",
    label: "Architect",
    tagline: "Customized Home Design",
    headline: "Architect — Customized Home Design",
    blurb: "Layouts, elevations, sections, kitchen + toilet details and full space planning.",
    services: [
      "Customized Layout Plans",
      "Floor Plans",
      "Elevation Design",
      "Section Drawings",
      "Toilet Details",
      "Kitchen Details",
      "Space Planning",
    ],
    Icon: Compass,
    accent: "#0b2545", // navy
    chipClass: "bg-navy text-gold border-navy",
  },
  {
    key: "structural",
    label: "Structural Engineer",
    tagline: "Structural Drawings & Safety",
    headline: "Structural Engineer — Structural Drawings & Safety",
    blurb: "Foundations, columns, beams, framing and slab reinforcement — built to code.",
    services: [
      "Foundation Drawing",
      "Raft / Foundation Details",
      "Column Schedule",
      "Framing Plan",
      "Beam Reinforcement",
      "Slab Reinforcement",
    ],
    Icon: ShieldCheck,
    accent: "#5b8c5a", // sage
    chipClass: "bg-emerald-700 text-white border-emerald-800",
  },
  {
    key: "mep",
    label: "MEP Engineer",
    tagline: "Utility & Services Planning",
    headline: "MEP Engineer — Utility & Services Planning",
    blurb: "Electrical, plumbing and HVAC layouts coordinated for service safety.",
    services: [
      "Electrical Layout",
      "Plumbing Layout",
      "HVAC Layout",
      "Service Coordination Drawings",
    ],
    Icon: Zap,
    accent: "#b46329", // copper
    chipClass: "bg-orange-700 text-white border-orange-800",
  },
  {
    key: "interior",
    label: "Interior Designer",
    tagline: "Interior Planning & Styling",
    headline: "Interior Designer — Interior Planning & Styling",
    blurb: "Bedroom, living, kitchen and toilet interiors with lighting + furniture layout.",
    services: [
      "Toilet Interior Design",
      "Kitchen Interior Design",
      "Bedroom Interior Concepts",
      "Living Room Interior Concepts",
      "Ceiling & Lighting Concepts",
      "Furniture Layout Suggestions",
    ],
    Icon: Armchair,
    accent: "#7b3b8d", // royal
    chipClass: "bg-fuchsia-700 text-white border-fuchsia-800",
  },
];

// Future-proof — used to gracefully render an unknown category.
export const FUTURE_CATEGORIES = ["contractor", "vastu", "landscape", "site_supervisor"];

export const CATEGORY_BY_KEY = Object.fromEntries(PRO_CATEGORIES.map((c) => [c.key, c]));

export const CategoryIcon = ({ category, className = "h-4 w-4" }) => {
  const meta = CATEGORY_BY_KEY[category];
  const Icon = meta?.Icon || HardHat;
  return <Icon className={className} aria-hidden />;
};

export const categoryLabel = (key) => CATEGORY_BY_KEY[key]?.label || (key === "all" ? "All categories" : "Other");
export const categoryHeadline = (key) => CATEGORY_BY_KEY[key]?.headline || "Design Professional";

export const VerifiedBadge = ({ className = "" }) => (
  <span
    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-extrabold uppercase tracking-wider bg-emerald-100 text-emerald-800 border border-emerald-300 ${className}`}
    title="Verified by Team Mera Naksha"
  >
    <ShieldCheck className="h-3 w-3" /> Verified Professional
  </span>
);

export const FeaturedBadge = ({ className = "" }) => (
  <span
    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-extrabold uppercase tracking-wider bg-gold/15 text-gold-dark border border-gold/40 ${className}`}
  >
    <Sparkles className="h-3 w-3" /> Featured
  </span>
);
